/**
 * TherapistPortal.tsx - Portal principal para terapeutas
 * 
 * 🧑‍⚕️ Portal completo que combina:
 * - Validación por RUT con diseño minimalista
 * - Dashboard profesional integrado
 * - Gestión de pacientes y consultas
 * - Interface ergonómica y profesional
 * 
 * @version 1.0.0
 * @author Conectemos Chile
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import TherapistVerification from '../components/TherapistAuth/TherapistVerification';
import TherapistCalendarComponent from '../components/TherapistCalendar/TherapistCalendar';
import ClinicCalendarComponent from '../components/ClinicCalendar/ClinicCalendar';
import ClinicalRecordsModule from '../components/TherapistPatients/ClinicalRecords';
import ConsultationsModule from '../components/TherapistConsultations/Consultations';
import StatisticsViewComponent from '../components/TherapistStatistics/StatisticsView';
import InvestorOverview from '../components/InvestorPortal/InvestorOverview';
import DirectorOverview from '../components/DirectorPortal/DirectorOverview';
import InvestorCalendar from '../components/InvestorPortal/InvestorCalendar';
import InvestorPatients from '../components/InvestorPortal/InvestorPatients';
import InvestorConsultas from '../components/InvestorPortal/InvestorConsultas';
import InvestorRevenue from '../components/InvestorPortal/InvestorRevenue';
import CobrosView from '../components/DirectorPortal/CobrosView';
import Cementerio from '../components/DirectorPortal/Cementerio';
import BitacoraSistema from '../components/DirectorPortal/BitacoraSistema';
import PatientCredentials from '../components/DirectorPortal/PatientCredentials';
import PendingPaymentsWidget from '../components/PendingPayments/PendingPaymentsWidget';
import DashboardMetricModal from '../components/TherapistDashboard/DashboardMetricModal';
import TherapistValidationService, { AuthenticationResult, InvestorProfile, DashboardMetricKey } from '../services/therapist-validation.service';
import { supabase } from '../lib/supabase/client';
import { ThemeProvider, useTheme } from '../contexts/ThemeContext';
import { Skeleton } from '../components/ui/Skeleton';
import { clearAllFichaDrafts } from '../components/TherapistPatients/fichaPersistence';
import { toSantiagoISO, toSantiagoDateStr, toSantiagoTime } from '../lib/utils/dateUtils';
import './TherapistPortal.css';

// ============================================================================
// INTERFACES Y TIPOS
// ============================================================================

interface AuthenticatedTherapist {
  id: string;
  rut: string;
  nombre: string;
  apellido: string;
  email: string;
  especialidad: string;
  titulo?: string;
  registroProfesional?: string;
  numeroLicencia?: string;
  telefono?: string;
  estado: string;
  avatar?: string;
  rol: 'terapeuta' | 'director';
  meet_url?: string;
}

type AuthenticatedInvestor = InvestorProfile;

// Cementerio: no hay campo de rol para distinguir inversionistas, así que se
// gatea por id fijo del inversionista fundador (Juan Pablo).
const JUAN_PABLO_INVESTOR_ID = '2a893e1b-ac9b-4c9e-a9d1-404f07791c71';

interface TherapistStats {
  thisMonth: {
    consultations: number;
    revenue: number;
    completed: number;
    newPatients: number;
  };
  totalPatients: number;
  weeklyConsultations: number;
  upcomingConsultations: any[];
  nextConsultation: any;
}

type DashboardView =
  // Terapeuta / Director
  | 'overview' | 'calendar' | 'patients' | 'consultations'
  | 'statistics' | 'availability' | 'profile' | 'cashflow'
  | 'clinic_calendar' | 'admin_patients' | 'admin_consultations'
  // Director — Resumen Clínico BI (spec_dashboard_director_bi.md), vista propia
  // separada de 'overview' (que sigue siendo la vista personal del terapeuta)
  | 'director_overview'
  // Cobros y boletas SII — director ve toda la clínica, inversor ve su cartera
  | 'cobros'
  // Inversionista
  | 'investor_overview' | 'investor_calendar' | 'investor_patients'
  | 'investor_consultas' | 'investor_revenue'
  // Archivo — director e inversionista fundador, ver isJuanPabloInvestor
  | 'cementerio'
  // Auditoría — director e inversionista fundador, ver isJuanPabloInvestor
  | 'bitacora'
  // FUNC-124 — tracking del rollout SEC-064 (login paciente con PIN), solo director
  | 'director_credenciales';

// La vista activa vive en la URL: /panel/:view (ver docs/UX_VISION.md P1-6).
const PORTAL_BASE = '/panel';

const ALL_DASHBOARD_VIEWS: ReadonlySet<string> = new Set<DashboardView>([
  'overview', 'calendar', 'patients', 'consultations', 'statistics', 'availability',
  'profile', 'cashflow', 'clinic_calendar', 'admin_patients', 'admin_consultations',
  'director_overview', 'cobros', 'investor_overview', 'investor_calendar',
  'investor_patients', 'investor_consultas', 'investor_revenue', 'cementerio', 'bitacora',
  'director_credenciales',
]);

// Vistas que solo existen en el menú de "modo Director". Estar parado en una de
// ellas ES el modo director — `adminMode` se deriva de la vista, no es un toggle
// de estado aparte (así el modo sobrevive a un refresh / deep-link).
const ADMIN_VIEWS: ReadonlySet<string> = new Set<DashboardView>([
  'director_overview', 'clinic_calendar', 'admin_patients', 'admin_consultations',
  'cashflow', 'cobros', 'cementerio', 'bitacora', 'director_credenciales',
]);

/** ¿Este usuario puede ver esta vista? Usado para normalizar la URL. */
function canAccessView(
  view: DashboardView,
  ctx: { isDirector: boolean; isInvestor: boolean; isJuanPabloInvestor: boolean },
): boolean {
  if (ctx.isInvestor) {
    if (view === 'cementerio' || view === 'bitacora') return ctx.isJuanPabloInvestor;
    return view === 'cobros' || view.startsWith('investor_');
  }
  if (view.startsWith('investor_')) return false;
  if (ctx.isDirector) return true; // director tiene el menú completo en ambos modos
  // terapeuta estándar (fase 1): menú reducido + su propio perfil
  return view === 'overview' || view === 'calendar' || view === 'patients' || view === 'consultations'
    || view === 'profile';
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const TherapistPortal: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userType, setUserType] = useState<'therapist' | 'investor'>('therapist');
  const [authenticatedTherapist, setAuthenticatedTherapist] = useState<AuthenticatedTherapist | null>(null);
  const [authenticatedInvestor, setAuthenticatedInvestor] = useState<AuthenticatedInvestor | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [therapistStats, setTherapistStats] = useState<TherapistStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState<string | undefined>(undefined);

  const navigate = useNavigate();
  const { view: rawView } = useParams<{ view?: string }>();
  const mainRef = useRef<HTMLElement>(null);

  // Vista activa: derivada de la URL, no de un useState. `/panel` sin :view cae
  // al default según el tipo de usuario.
  const defaultView: DashboardView = userType === 'investor' ? 'investor_overview' : 'overview';
  const currentView: DashboardView =
    rawView && ALL_DASHBOARD_VIEWS.has(rawView) ? (rawView as DashboardView) : defaultView;

  const goToView = (view: DashboardView) => {
    navigate(`${PORTAL_BASE}/${view}`);
    if (isMobile) setSidebarCollapsed(false);
  };

  // ============================================================================
  // EFECTOS
  // ============================================================================

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Restaurar sesión activa de Supabase Auth al montar el portal
  useEffect(() => {
    TherapistValidationService.getTherapistFromSession().then(result => {
      if (result.found) {
        if (result.userType === 'investor' && result.investor) {
          setUserType('investor');
          setAuthenticatedInvestor(result.investor as AuthenticatedInvestor);
          setIsAuthenticated(true);
          // La vista sale de la URL — un deep-link (/panel/investor_calendar)
          // sobrevive al refresh; /panel a secas cae al default.
        } else if (result.therapist) {
          setUserType('therapist');
          setAuthenticatedTherapist(result.therapist as AuthenticatedTherapist);
          setIsAuthenticated(true);
          loadTherapistStats(result.therapist.id);
        }
      } else if (result.message) {
        setAuthError(result.message);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        clearAllFichaDrafts(); // P1-9 — no dejar borradores de ficha en un PC compartido
        setIsAuthenticated(false);
        setAuthenticatedTherapist(null);
        setAuthenticatedInvestor(null);
        setTherapistStats(null);
        setUserType('therapist');
        navigate(PORTAL_BASE, { replace: true });
      }
    });

    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (authenticatedTherapist) {
      loadTherapistStats();
    }
  }, [authenticatedTherapist]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================================
  // MANEJADORES
  // ============================================================================

  const handleAuthenticated = async (result: AuthenticationResult) => {
    // La vista de aterrizaje la resuelve el efecto de normalización de la URL
    // (deep-link accesible → se respeta; si no → default del rol).
    if (result.userType === 'investor' && result.investor) {
      setUserType('investor');
      setAuthenticatedInvestor(result.investor as AuthenticatedInvestor);
      setIsAuthenticated(true);
    } else if (result.therapist) {
      setUserType('therapist');
      setAuthenticatedTherapist(result.therapist as AuthenticatedTherapist);
      setIsAuthenticated(true);
      await loadTherapistStats(result.therapist.id);
    }
  };

  const loadTherapistStats = async (therapistId?: string) => {
    if (!therapistId && !authenticatedTherapist) return;
    
    setLoading(true);
    try {
      const stats = await TherapistValidationService.getTherapistStats(
        therapistId || authenticatedTherapist!.id
      );
      setTherapistStats(stats);
    } catch (error) {
      console.error('❌ Error cargando estadísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (toAdmin: boolean) => {
    // `adminMode` se deriva de la vista, así que cambiar de modo = navegar a la
    // vista de arranque de ese modo.
    goToView(toAdmin ? 'clinic_calendar' : 'overview');
  };

  const handleLogout = async () => {
    clearAllFichaDrafts(); // P1-9 — borrar borradores de ficha antes de soltar la sesión
    await supabase.auth.signOut();
    setIsAuthenticated(false);
    setAuthenticatedTherapist(null);
    setAuthenticatedInvestor(null);
    setTherapistStats(null);
    setUserType('therapist');
    navigate(PORTAL_BASE, { replace: true });
  };

  // ============================================================================
  // CONFIGURACIÓN DE MENÚ
  // ============================================================================

  const isDirector = authenticatedTherapist?.rol === 'director';
  const isInvestor = userType === 'investor';
  const isJuanPabloInvestor = isInvestor && authenticatedInvestor?.id === JUAN_PABLO_INVESTOR_ID;

  // `adminMode` (director trabajando como director vs. como terapeuta) se deriva
  // de la vista activa — no es estado aparte, así sobrevive a refresh/deep-link.
  const adminMode = isDirector && ADMIN_VIEWS.has(currentView);

  // Normalizar la URL a un `/panel/<vista>` canónico y accesible:
  //  - `/panel` a secas → `/panel/<default del rol>`
  //  - vista desconocida o sin acceso para este usuario → default del rol
  useEffect(() => {
    if (!isAuthenticated) return;
    const needsRedirect =
      !rawView ||
      !ALL_DASHBOARD_VIEWS.has(rawView) ||
      !canAccessView(currentView, { isDirector, isInvestor, isJuanPabloInvestor });
    if (needsRedirect && rawView !== defaultView) {
      navigate(`${PORTAL_BASE}/${defaultView}`, { replace: true });
    }
  }, [isAuthenticated, rawView, currentView, defaultView, isDirector, isInvestor, isJuanPabloInvestor, navigate]);

  // Al cambiar de vista: resetear el scroll y mover el foco a la región de
  // contenido (anuncio para lectores de pantalla). Antes no pasaba nada — un SR
  // no se enteraba del cambio y en mobile quedabas a mitad de la vista anterior.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
    window.scrollTo({ top: 0 });
    mainRef.current?.focus();
  }, [currentView]);

  type MenuItem = { id: DashboardView; icon: string; label: string; badge: string | null } | { divider: true; label: string };

  // Menú completo — solo para terapeutas con rol director (Ángel Díaz, modo terapeuta)
  const therapistMenuItems: MenuItem[] = [
    { id: 'overview',         icon: 'fas fa-tachometer-alt', label: 'Dashboard',       badge: null },
    { id: 'calendar',         icon: 'fas fa-calendar-alt',   label: 'Mi Agenda',       badge: null },
    { divider: true, label: 'Clínica' },
    { id: 'patients',         icon: 'fas fa-users',          label: 'Mis Pacientes',   badge: therapistStats?.totalPatients?.toString() || null },
    { id: 'consultations',    icon: 'fas fa-clipboard-list',  label: 'Consultas',       badge: null },
    { id: 'statistics',       icon: 'fas fa-chart-bar',       label: 'Estadísticas',    badge: null },
    { divider: true, label: 'Configuración' },
    { id: 'availability',     icon: 'fas fa-clock',          label: 'Horarios',        badge: null },
    { id: 'profile',          icon: 'fas fa-user-cog',       label: 'Mi Perfil',       badge: null },
  ];

  // Menú reducido — terapeutas estándar (producción fase 1)
  const limitedMenuItems: MenuItem[] = [
    { id: 'overview',         icon: 'fas fa-tachometer-alt', label: 'Dashboard',       badge: null },
    { id: 'calendar',         icon: 'fas fa-calendar-alt',   label: 'Mi Agenda',       badge: null },
    { divider: true, label: 'Clínica' },
    { id: 'patients',         icon: 'fas fa-users',          label: 'Mis Pacientes',   badge: therapistStats?.totalPatients?.toString() || null },
    { id: 'consultations',   icon: 'fas fa-clipboard-list',  label: 'Consultas',       badge: null },
    { divider: true, label: 'Configuración' },
    { id: 'profile',          icon: 'fas fa-user-cog',       label: 'Mi Perfil',       badge: null },
  ];

  const adminMenuItems: MenuItem[] = [
    { id: 'director_overview',      icon: 'fas fa-chart-pie',      label: 'Resumen Clínico',     badge: null },
    { divider: true, label: 'Clínica Global' },
    { id: 'clinic_calendar',        icon: 'fas fa-calendar-check', label: 'Agenda Clínica',      badge: null },
    { id: 'admin_patients',         icon: 'fas fa-users-cog',      label: 'Todos los Pacientes', badge: null },
    { id: 'director_credenciales',  icon: 'fas fa-key',            label: 'Credenciales',        badge: null },
    { id: 'admin_consultations',    icon: 'fas fa-clipboard-list', label: 'Todas las Consultas', badge: null },
    { divider: true, label: 'Finanzas' },
    { id: 'cashflow',               icon: 'fas fa-receipt',        label: 'Flujo de Caja',       badge: null },
    { id: 'cobros',                 icon: 'fas fa-money-bill-transfer', label: 'Cobros y Boletas', badge: null },
    { divider: true, label: 'Archivo' },
    { id: 'cementerio',             icon: 'fas fa-cross',          label: 'Cementerio',          badge: null },
    { divider: true, label: 'Auditoría' },
    { id: 'bitacora',               icon: 'fas fa-shield-halved',  label: 'Bitácora del Sistema', badge: null },
    { divider: true, label: 'Configuración' },
    { id: 'profile',                icon: 'fas fa-user-cog',       label: 'Mi Perfil',           badge: null },
  ];

  const investorMenuItems: MenuItem[] = [
    { id: 'investor_overview',   icon: 'fas fa-tachometer-alt',   label: 'Dashboard',    badge: null },
    { id: 'investor_calendar',   icon: 'fas fa-calendar-alt',     label: 'Agenda',       badge: null },
    { divider: true, label: 'Mi Cartera' },
    { id: 'investor_patients',   icon: 'fas fa-users',            label: 'Mis Pacientes', badge: null },
    { id: 'investor_consultas',  icon: 'fas fa-clipboard-list',   label: 'Consultas',    badge: null },
    { id: 'investor_revenue',    icon: 'fas fa-chart-line',       label: 'Ingresos',     badge: null },
    { id: 'cobros',              icon: 'fas fa-money-bill-transfer', label: 'Cobros',    badge: null },
    ...(isJuanPabloInvestor
      ? [{ divider: true, label: 'Archivo' } as MenuItem,
         { id: 'cementerio', icon: 'fas fa-cross', label: 'Cementerio', badge: null } as MenuItem,
         { divider: true, label: 'Auditoría' } as MenuItem,
         { id: 'bitacora', icon: 'fas fa-shield-halved', label: 'Bitácora del Sistema', badge: null } as MenuItem]
      : []),
  ];

  // Director: menú completo en ambos modos
  // Terapeuta estándar: menú reducido
  // Inversionista: menú propio
  const menuItems = isInvestor
    ? investorMenuItems
    : isDirector
      ? (adminMode ? adminMenuItems : therapistMenuItems)
      : limitedMenuItems;

  const currentViewLabel =
    (menuItems.find(item => !('divider' in item) && item.id === currentView) as
      | Extract<MenuItem, { id: DashboardView }>
      | undefined)?.label ?? 'Portal';

  // ============================================================================
  // RENDER DE CONTENIDO
  // ============================================================================

  const renderMainContent = () => {
    // ── Investor views ────────────────────────────────────────────────────────
    if (isInvestor && authenticatedInvestor) {
      switch (currentView) {
        case 'investor_overview':
          return <InvestorOverview investor={authenticatedInvestor} />;
        case 'investor_calendar':
          return <InvestorCalendar investor={authenticatedInvestor} />;
        case 'investor_patients':
          return <InvestorPatients investor={authenticatedInvestor} isFounder={isJuanPabloInvestor} />;
        case 'investor_consultas':
          return <InvestorConsultas investor={authenticatedInvestor} />;
        case 'investor_revenue':
          return <InvestorRevenue investor={authenticatedInvestor} />;
        case 'cobros':
          return (
            <CobrosView
              therapistId={authenticatedInvestor.id}
              isDirector={false}
              inversionistaId={authenticatedInvestor.id}
            />
          );
        case 'cementerio':
          if (!isJuanPabloInvestor) return <InvestorOverview investor={authenticatedInvestor} />;
          return <Cementerio />;
        case 'bitacora':
          if (!isJuanPabloInvestor) return <InvestorOverview investor={authenticatedInvestor} />;
          return <BitacoraSistema />;
        default:
          return <InvestorOverview investor={authenticatedInvestor} />;
      }
    }

    if (!authenticatedTherapist) return null;

    // Crear objeto compatible con las interfaces de los componentes existentes
    const compatibleTherapist = {
      ...authenticatedTherapist,
      name: `${authenticatedTherapist.nombre} ${authenticatedTherapist.apellido}`
    };

    switch (currentView) {
      case 'overview':
        return <DashboardOverview therapist={authenticatedTherapist} stats={therapistStats} />;
      case 'calendar':
        return <TherapistCalendarComponent therapist={compatibleTherapist} />;
      case 'patients':
        return <ClinicalRecordsModule therapist={authenticatedTherapist} compactRegistration={isDirector} />;
      case 'consultations':
        return <ConsultationsModule therapist={authenticatedTherapist} isDirector={isDirector} />;
      case 'statistics':
        return <StatisticsViewComponent therapist={compatibleTherapist} />;
      case 'clinic_calendar':
        if (!isDirector) return <DashboardOverview therapist={authenticatedTherapist} stats={therapistStats} />;
        return <ClinicCalendarComponent currentDirectorId={authenticatedTherapist.id} />;
      case 'cashflow':
        if (!isDirector) return <DashboardOverview therapist={authenticatedTherapist} stats={therapistStats} />;
        return <CashFlowView therapist={authenticatedTherapist} />;
      case 'director_overview':
        if (!isDirector) return <DashboardOverview therapist={authenticatedTherapist} stats={therapistStats} />;
        return <DirectorOverview />;
      case 'cobros':
        if (!isDirector) return <DashboardOverview therapist={authenticatedTherapist} stats={therapistStats} />;
        return <CobrosView therapistId={authenticatedTherapist.id} isDirector={true} />;
      case 'cementerio':
        if (!isDirector) return <DashboardOverview therapist={authenticatedTherapist} stats={therapistStats} />;
        return <Cementerio />;
      case 'bitacora':
        if (!isDirector) return <DashboardOverview therapist={authenticatedTherapist} stats={therapistStats} />;
        return <BitacoraSistema />;
      case 'admin_patients':
        if (!isDirector || !adminMode) return <DashboardOverview therapist={authenticatedTherapist} stats={therapistStats} />;
        return <ClinicalRecordsModule therapist={authenticatedTherapist} adminMode={true} />;
      case 'admin_consultations':
        if (!isDirector || !adminMode) return <DashboardOverview therapist={authenticatedTherapist} stats={therapistStats} />;
        return <ConsultationsModule therapist={authenticatedTherapist} adminMode={true} isDirector={isDirector} />;
      case 'director_credenciales':
        if (!isDirector || !adminMode) return <DashboardOverview therapist={authenticatedTherapist} stats={therapistStats} />;
        return <PatientCredentials />;
      case 'availability':
        return <AvailabilityConfig therapist={authenticatedTherapist} />;
      case 'profile':
        return <TherapistProfile therapist={authenticatedTherapist} onLogout={handleLogout} />;
      default:
        return <DashboardOverview therapist={authenticatedTherapist} stats={therapistStats} />;
    }
  };

  // ============================================================================
  // RENDER PRINCIPAL
  // ============================================================================

  if (!isAuthenticated) {
    return (
      <>
        <Helmet>
          <title>Portal Profesional - Conectemos Chile</title>
          <meta name="description" content="Acceso profesional para terapeutas de Conectemos Chile" />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <TherapistVerification onAuthenticated={handleAuthenticated} initialError={authError} />
      </>
    );
  }

  return (
    <ThemeProvider>
    <>
      <Helmet>
        <title>
          {isInvestor
            ? `${currentViewLabel} · Portal Inversor | Conectemos Chile`
            : `${currentViewLabel} · ${authenticatedTherapist?.nombre} ${authenticatedTherapist?.apellido} | Conectemos Chile`}
        </title>
        <meta name="description" content="Panel de trabajo profesional para gestión de pacientes y consultas" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className="therapist-portal">
        {/* Header del dashboard */}
        <header className="portal-header">
          <div className="header-left">
            <button 
              className="sidebar-toggle"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              title="Toggle menu"
            >
              <i className="fas fa-bars"></i>
            </button>
            <div className="portal-logo">
              <img
                src="/assets/img/logo.png"
                alt="Conectemos Chile"
                className="portal-logo__img"
                width="40"
                height="40"
              />
              <span className="portal-logo__text">
                <span className="logo-text">Conectemos Chile</span>
                <span className="logo-subtitle">Portal Profesional</span>
              </span>
            </div>
          </div>
          
          <div className="header-center">
            <h1 className="page-title">{currentViewLabel}</h1>
          </div>
          
          <div className="header-right">
            {isDirector && !isInvestor && (
              <div className="header-mode-switch">
                <button
                  className={`mode-switch-btn${!adminMode ? ' active' : ''}`}
                  onClick={() => switchMode(false)}
                  title="Modo Terapeuta"
                >
                  <i className="fas fa-user-md" />
                </button>
                <button
                  className={`mode-switch-btn${adminMode ? ' active' : ''}`}
                  onClick={() => switchMode(true)}
                  title="Modo Director"
                >
                  <i className="fas fa-clinic-medical" />
                </button>
              </div>
            )}
            <div className="therapist-info">
              <div className="therapist-details">
                <div className="therapist-name-row">
                  <span className="therapist-name">
                    {isInvestor
                      ? `${authenticatedInvestor?.nombre} ${authenticatedInvestor?.apellido}`
                      : `${authenticatedTherapist?.nombre} ${authenticatedTherapist?.apellido}`}
                  </span>
                  {isDirector && <span className="director-badge">Director</span>}
                  {isInvestor && <span className="director-badge director-badge--investor">Inversor</span>}
                </div>
                <span className="therapist-role">
                  {isInvestor
                    ? `${authenticatedInvestor?.tipo_inversionista} · ${authenticatedInvestor?.porcentaje_participacion}%`
                    : isDirector
                      ? 'Director Clínico'
                      : authenticatedTherapist?.especialidad?.split(',')[0]?.trim()}
                </span>
              </div>
            </div>
            
            <button
              className="logout-btn"
              onClick={handleLogout}
              title="Cerrar sesión"
            >
              <i className="fas fa-sign-out-alt"></i>
            </button>
          </div>
        </header>

        <div className="portal-body">
          {/* Sidebar de navegación */}
          <aside className={`portal-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
            <nav className="sidebar-nav">
              {menuItems.map((item, idx) => {
                if ('divider' in item) {
                  return (
                    <div key={`divider-${idx}`} className="nav-section-label">
                      {item.label}
                    </div>
                  );
                }
                return (
                  <button
                    key={item.id}
                    className={`nav-item ${currentView === item.id ? 'active' : ''}`}
                    aria-current={currentView === item.id ? 'page' : undefined}
                    onClick={() => goToView(item.id as DashboardView)}
                    title={item.label}
                  >
                    <i className={item.icon}></i>
                    <span className="nav-label">{item.label}</span>
                    {item.badge && (
                      <span className="nav-badge">{item.badge}</span>
                    )}
                  </button>
                );
              })}
            </nav>
            
            {(!sidebarCollapsed || isMobile) && !isInvestor && therapistStats && (
              <div className="sidebar-footer">
                <div className="quick-stats">
                  <h4>Resumen</h4>
                  <div className="stat-item">
                    <i className="fas fa-calendar-check"></i>
                    <span>{therapistStats.thisMonth.consultations} consultas este mes</span>
                  </div>
                  <div className="stat-item">
                    <i className="fas fa-users"></i>
                    <span>{therapistStats.totalPatients} pacientes totales</span>
                  </div>
                  {therapistStats.nextConsultation && (
                    <div className="stat-item">
                      <i className="fas fa-clock"></i>
                      <span>
                        Próxima: {new Date(therapistStats.nextConsultation.fecha_hora).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </aside>

          {/* Contenido principal */}
          <main
            className="portal-main"
            ref={mainRef}
            tabIndex={-1}
            aria-label={currentViewLabel}
          >
            {loading ? (
              <div className="portal-skeleton" role="status" aria-label="Cargando el portal">
                <Skeleton width={280} height={22} />
                <div className="portal-skeleton__kpis">
                  <Skeleton height={92} radius={14} />
                  <Skeleton height={92} radius={14} />
                  <Skeleton height={92} radius={14} />
                  <Skeleton height={92} radius={14} />
                </div>
                <Skeleton height={420} radius={16} />
              </div>
            ) : (
              renderMainContent()
            )}
          </main>
        </div>
        {!isInvestor && authenticatedTherapist && (
          <PendingPaymentsWidget
            therapistId={authenticatedTherapist.id}
            isDirector={isDirector}
          />
        )}
      </div>
    </>
    </ThemeProvider>
  );
};

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

const DashboardOverview: React.FC<{
  therapist: AuthenticatedTherapist;
  stats: TherapistStats | null;
}> = ({ therapist, stats }) => {
  const compatibleTherapist = { ...therapist, name: `${therapist.nombre} ${therapist.apellido}` };

  // Drill-down: qué KPI card está abierta en detalle (null = ninguna).
  const [openMetric, setOpenMetric] = useState<DashboardMetricKey | null>(null);

  const todayRaw = new Date().toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' });
  const todayLabel = todayRaw.charAt(0).toUpperCase() + todayRaw.slice(1);

  return (
    <div className="dashboard-home dashboard-home--therapist">

      {/* ── Saludo slim — una línea, sin card: la agenda del día es lo primero
          que tiene que quedar a la vista (avatar y detalle viven en "Mi Perfil") ── */}
      <div className="dashboard-greeting">
        <h2 className="dashboard-greeting__title">
          Hola, {therapist.nombre}
          {therapist.rol === 'director' && (
            <span className="director-badge">Director</span>
          )}
        </h2>
        <p className="dashboard-greeting__date">{todayLabel}</p>
      </div>

      {/* ── Métricas — cada card abre el detalle fila-a-fila del número (DashboardMetricModal) ── */}
      {stats && (
        <div className="overview-metrics">
          <button
            type="button"
            className="metric-card primary"
            onClick={() => setOpenMetric('consultations')}
            aria-haspopup="dialog"
          >
            <div className="metric-icon"><i className="fas fa-calendar-day" /></div>
            <div className="metric-content">
              <h3>{stats.thisMonth.consultations}</h3>
              <p>Consultas este mes</p>
            </div>
            <i className="fas fa-chevron-right metric-card__more" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="metric-card success"
            onClick={() => setOpenMetric('newPatients')}
            aria-haspopup="dialog"
          >
            <div className="metric-icon"><i className="fas fa-user-plus" /></div>
            <div className="metric-content">
              <h3>{stats.thisMonth.newPatients}</h3>
              <p>Pacientes nuevos este mes</p>
            </div>
            <i className="fas fa-chevron-right metric-card__more" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="metric-card warning"
            onClick={() => setOpenMetric('weekly')}
            aria-haspopup="dialog"
          >
            <div className="metric-icon"><i className="fas fa-clock" /></div>
            <div className="metric-content">
              <h3>{stats.weeklyConsultations}</h3>
              <p>Consultas agendadas esta semana</p>
            </div>
            <i className="fas fa-chevron-right metric-card__more" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="metric-card info"
            onClick={() => setOpenMetric('revenue')}
            aria-haspopup="dialog"
          >
            <div className="metric-icon"><i className="fas fa-dollar-sign" /></div>
            <div className="metric-content">
              <h3>${(stats.thisMonth.revenue / 1000).toFixed(0)}K</h3>
              <p>Ingresos del mes</p>
            </div>
            <i className="fas fa-chevron-right metric-card__more" aria-hidden="true" />
          </button>
        </div>
      )}

      {openMetric && (
        <DashboardMetricModal
          therapistId={therapist.id}
          metric={openMetric}
          onClose={() => setOpenMetric(null)}
        />
      )}

      {/* ── Agenda (incluye síntesis del día seleccionado, ver TherapistCalendar) ── */}
      <div className="overview-calendar-section">
        <TherapistCalendarComponent therapist={compatibleTherapist} />
      </div>
    </div>
  );
};

// ============================================================================
// MÓDULO: CONFIGURACIÓN DE HORARIOS
// ============================================================================


interface BlockedPeriod {
  id: string;
  terapeuta_id: string;
  fecha_inicio: string;
  fecha_fin: string | null;
  hora_inicio: string | null;
  hora_fin: string | null;
  todo_el_dia: boolean;
  tipo: string;
  motivo: string | null;
  activo: boolean;
}

interface BlockedPeriodHistoryEntry {
  id: string;
  blocked_period_id: string;
  accion: 'creado' | 'modificado' | 'eliminado';
  fecha_inicio: string | null;
  fecha_fin: string | null;
  hora_inicio: string | null;
  hora_fin: string | null;
  todo_el_dia: boolean | null;
  tipo: string | null;
  motivo: string | null;
  realizado_por_nombre: string | null;
  created_at: string;
}

const TIPO_BLOQUEO_LABELS: Record<string, string> = {
  vacaciones: 'Vacaciones',
  personal: 'Personal',
  capacitacion: 'Capacitación',
  enfermedad: 'Enfermedad',
  otro: 'Otro',
};

// FUNC-097 punto 5 — 'modificado' está reservado a futuro: el trigger de BD
// (trg_log_blocked_period_change, sql/schema_v2.sql ~1916-1964) SÍ escribe esta
// acción ante un UPDATE de therapist_blocked_periods, pero hoy no existe UI de
// edición de bloqueos (solo alta y baja) — nada dispara esa rama. Se mantiene
// el mapeo completo a propósito (Record exhaustivo sobre el CHECK real de la
// columna en BD) para no dejar un label sin traducir si algún día se agrega
// edición, o si alguien actualiza una fila a mano en Supabase. Decisión:
// documentar, no implementar edición de bloqueos (no fue pedida).
const ACCION_HISTORIAL_LABELS: Record<BlockedPeriodHistoryEntry['accion'], string> = {
  creado: 'Bloqueo creado',
  modificado: 'Bloqueo modificado',
  eliminado: 'Bloqueo eliminado',
};

const formatRangoBloqueo = (bp: {
  fecha_inicio: string | null;
  fecha_fin: string | null;
  hora_inicio: string | null;
  hora_fin: string | null;
  todo_el_dia: boolean | null;
}): string => {
  if (!bp.fecha_inicio) return '—';

  const fmt = (iso: string) =>
    new Date(iso + 'T12:00:00').toLocaleDateString('es-CL', {
      weekday: 'short', day: 'numeric', month: 'long', year: 'numeric',
    });

  const rangoFecha = bp.fecha_fin && bp.fecha_fin !== bp.fecha_inicio
    ? `${fmt(bp.fecha_inicio)} – ${fmt(bp.fecha_fin)}`
    : fmt(bp.fecha_inicio);

  const rangoHora = bp.todo_el_dia
    ? 'Día completo'
    : (bp.hora_inicio && bp.hora_fin ? `${bp.hora_inicio.slice(0, 5)} – ${bp.hora_fin.slice(0, 5)}` : 'Día completo');

  return `${rangoFecha} · ${rangoHora}`;
};

// FUNC-097 punto 4 — mismo criterio que TerapeutaOption en DirectorOverview.tsx:
// lectura llana a `terapeutas` (catálogo, no dato sensible), sin RPC nueva —
// el director ya puede leer esta tabla directo.
interface TerapeutaOption {
  id: string;
  nombre: string;
  apellido: string;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD' local a partir de componentes de fecha — mismo criterio que
 *  MonthPicker (src/components/ui/MonthPicker.tsx): nunca Date.toISOString(),
 *  que convierte a UTC y corre un día en huso horario negativo. */
const ymdLocal = (y: number, m: number, d: number): string => `${y}-${pad2(m + 1)}-${pad2(d)}`;

/** Expande un bloqueo (fecha_inicio..fecha_fin, o solo fecha_inicio si no hay
 *  rango) en la lista de días 'YYYY-MM-DD' que cubre — usado para pintar el
 *  calendario de FUNC-097 punto 1. Tope de 366 iteraciones como guarda ante
 *  datos corruptos (fecha_fin absurdamente lejana). */
const expandBlockedDates = (bp: { fecha_inicio: string; fecha_fin: string | null }): string[] => {
  const end = bp.fecha_fin || bp.fecha_inicio;
  const dates: string[] = [];
  let cur = new Date(`${bp.fecha_inicio}T12:00:00`);
  const endDate = new Date(`${end}T12:00:00`);
  let guard = 0;
  while (cur.getTime() <= endDate.getTime() && guard < 366) {
    dates.push(ymdLocal(cur.getFullYear(), cur.getMonth(), cur.getDate()));
    cur = new Date(cur.getTime() + 86_400_000);
    guard++;
  }
  return dates;
};

const AVAIL_CAL_MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const AVAIL_CAL_WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

interface AvailCalCell {
  dateStr: string;
  day: number;
  isCurrentMonth: boolean;
}

/** Grilla fija de 6 semanas (42 celdas), lunes primero — mismo algoritmo que
 *  buildMonthGrid de MonthPicker, pero de solo lectura y con estados propios
 *  (bloqueado / con citas) que un picker de selección única no modela. No vale
 *  la pena generalizar MonthPicker para este caso de uso, que además hace su
 *  propio fetch de disponibilidad por mes. */
const buildAvailCalGrid = (year: number, month: number): AvailCalCell[] => {
  const firstOfMonth = new Date(year, month, 1);
  const leadingBlanks = (firstOfMonth.getDay() + 6) % 7; // Lun=0 … Dom=6
  const start = new Date(year, month, 1 - leadingBlanks);
  const cells: AvailCalCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    cells.push({
      dateStr: ymdLocal(d.getFullYear(), d.getMonth(), d.getDate()),
      day: d.getDate(),
      isCurrentMonth: d.getMonth() === month,
    });
  }
  return cells;
};

interface AvailabilityCalendarProps {
  /** Bloqueos activos del terapeuta objetivo — ya cargados por el padre
   *  (AvailabilityConfig), sin query propia. */
  blockedPeriods: BlockedPeriod[];
  targetTherapistId: string;
  onDayClick: (dateStr: string) => void;
}

/**
 * FUNC-097 punto 1 — vista mensual de solo lectura: qué días están bloqueados
 * (punto rojo, de `blockedPeriods` ya cargado) y qué días tienen citas
 * agendadas (punto azul, fetch propio a `consultas` acotado al mes visible —
 * mismo patrón de select directo que ya usa TherapistCalendar.tsx, sin RPC
 * nueva). Clic en un día del mes actual prellena la fecha del formulario de
 * abajo. Días fuera del mes visible u anteriores a hoy no son clickeables.
 */
const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({
  blockedPeriods,
  targetTherapistId,
  onDayClick,
}) => {
  const [viewDate, setViewDate] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [busyDays, setBusyDays] = useState<Map<string, number>>(new Map());
  const [busyStatus, setBusyStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');

  useEffect(() => {
    let active = true;
    setBusyStatus('loading');
    (async () => {
      const monthStart = `${viewDate.year}-${pad2(viewDate.month + 1)}-01`;
      const nextMonthDate = new Date(viewDate.year, viewDate.month + 1, 1);
      const monthEndExclusive = ymdLocal(nextMonthDate.getFullYear(), nextMonthDate.getMonth(), nextMonthDate.getDate());

      const { data, error } = await supabase
        .from('consultas')
        .select('fecha_hora, estado')
        .eq('terapeuta_id', targetTherapistId)
        .gte('fecha_hora', `${monthStart}T00:00:00`)
        .lt('fecha_hora', `${monthEndExclusive}T00:00:00`)
        .neq('estado', 'cancelada');

      if (!active) return;
      if (error) {
        setBusyStatus('error');
        setBusyDays(new Map());
        return;
      }

      const map = new Map<string, number>();
      for (const row of data ?? []) {
        const dateStr = toSantiagoDateStr(new Date(row.fecha_hora));
        map.set(dateStr, (map.get(dateStr) ?? 0) + 1);
      }
      setBusyDays(map);
      setBusyStatus('loaded');
    })();
    return () => { active = false; };
  }, [targetTherapistId, viewDate.year, viewDate.month]);

  const blockedDaySet = useMemo(() => {
    const set = new Set<string>();
    for (const bp of blockedPeriods) {
      for (const d of expandBlockedDates(bp)) set.add(d);
    }
    return set;
  }, [blockedPeriods]);

  const grid = useMemo(() => buildAvailCalGrid(viewDate.year, viewDate.month), [viewDate.year, viewDate.month]);
  const today = useMemo(() => {
    const d = new Date();
    return ymdLocal(d.getFullYear(), d.getMonth(), d.getDate());
  }, []);

  const shiftMonth = (delta: number) => {
    setViewDate(prev => {
      const d = new Date(prev.year, prev.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  return (
    <div className="avail-cal">
      <div className="avail-cal__header">
        <button type="button" className="avail-cal__nav" onClick={() => shiftMonth(-1)} aria-label="Mes anterior">
          <i className="fas fa-chevron-left" />
        </button>
        <span className="avail-cal__title" aria-live="polite">
          {AVAIL_CAL_MONTH_NAMES[viewDate.month]} {viewDate.year}
        </span>
        <button type="button" className="avail-cal__nav" onClick={() => shiftMonth(1)} aria-label="Mes siguiente">
          <i className="fas fa-chevron-right" />
        </button>
      </div>

      <div className="avail-cal__grid" role="grid" aria-label="Calendario de disponibilidad">
        {AVAIL_CAL_WEEKDAYS.map(d => (
          <span key={d} className="avail-cal__weekday">{d}</span>
        ))}
        {grid.map(cell => {
          const isBlocked = cell.isCurrentMonth && blockedDaySet.has(cell.dateStr);
          const busyCount = cell.isCurrentMonth ? (busyDays.get(cell.dateStr) ?? 0) : 0;
          const isPast = cell.dateStr < today;
          const clickable = cell.isCurrentMonth && !isPast;
          const stateLabel = isBlocked && busyCount > 0
            ? 'bloqueado, con citas agendadas'
            : isBlocked
              ? 'bloqueado'
              : busyCount > 0
                ? `${busyCount} cita${busyCount > 1 ? 's' : ''} agendada${busyCount > 1 ? 's' : ''}`
                : 'libre';

          return (
            <button
              key={cell.dateStr}
              type="button"
              className={
                'avail-cal__day' +
                (!cell.isCurrentMonth ? ' is-outside' : '') +
                (cell.dateStr === today ? ' is-today' : '')
              }
              disabled={!clickable}
              onClick={() => onDayClick(cell.dateStr)}
              title={cell.isCurrentMonth ? `${cell.dateStr} · ${stateLabel}` : undefined}
              aria-label={cell.isCurrentMonth ? `Día ${cell.day}, ${stateLabel}` : undefined}
            >
              <span className="avail-cal__day-num">{cell.day}</span>
              {(isBlocked || busyCount > 0) && (
                <span className="avail-cal__dots">
                  {isBlocked && <span className="avail-cal__dot avail-cal__dot--blocked" />}
                  {busyCount > 0 && <span className="avail-cal__dot avail-cal__dot--busy" />}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="avail-cal__legend">
        <span className="avail-cal__legend-item">
          <span className="avail-cal__dot avail-cal__dot--blocked" /> Bloqueado
        </span>
        <span className="avail-cal__legend-item">
          <span className="avail-cal__dot avail-cal__dot--busy" /> Con citas
        </span>
        {busyStatus === 'error' && (
          <span className="avail-cal__legend-item avail-cal__legend-item--error">
            No se pudieron cargar las citas del mes
          </span>
        )}
      </div>
    </div>
  );
};

const AvailabilityConfig: React.FC<{ therapist: AuthenticatedTherapist }> = ({ therapist }) => {
  const isDirector = therapist.rol === 'director';

  // FUNC-097 punto 4 — el director gestiona bloqueos de cualquier terapeuta
  // activo: RLS "director_ve_todos_bloqueos" es FOR ALL sin restricción de
  // terapeuta_id (sql/schema_v2.sql, sección 3.6), así que INSERT/DELETE para
  // otro terapeuta ya está permitido a nivel de base — acá solo se agrega la
  // UI para elegir a quién. Un terapeuta estándar no ve el selector y siempre
  // gestiona los suyos (targetTherapistId nunca cambia de therapist.id).
  const [terapeutasOptions, setTerapeutasOptions] = useState<TerapeutaOption[]>([]);
  const [targetTherapistId, setTargetTherapistId] = useState<string>(therapist.id);

  useEffect(() => {
    if (!isDirector) return;
    let active = true;
    (async () => {
      const { data, error } = await supabase
        .from('terapeutas')
        .select('id, nombre, apellido')
        .eq('activo', true)
        .order('apellido', { ascending: true })
        .order('nombre', { ascending: true });
      if (!active) return;
      if (error) {
        console.error('Error cargando lista de terapeutas:', error.code);
        return;
      }
      setTerapeutasOptions((data ?? []) as TerapeutaOption[]);
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirector]);

  const [blockedPeriods, setBlockedPeriods] = useState<BlockedPeriod[]>([]);
  const [newBlock, setNewBlock] = useState({
    fechaInicio: '',
    fechaFin: '',
    horaInicio: '',
    horaFin: '',
    motivo: '',
    tipo: 'personal',
    diaCompleto: false,
  });
  const [blockLoadStatus, setBlockLoadStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [blockError, setBlockError] = useState<string>('');
  const [blockSaveStatus, setBlockSaveStatus] = useState<'idle' | 'saving' | 'error'>('idle');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const [history, setHistory] = useState<BlockedPeriodHistoryEntry[]>([]);
  const [historyLoadStatus, setHistoryLoadStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [historyOpen, setHistoryOpen] = useState(false);

  // Ancla de scroll del formulario — clic en un día del calendario (punto 1)
  // prellena la fecha y trae el formulario a la vista, mismo patrón de doble
  // rAF que RescheduleForm.tsx (scrollIntoView en el mismo tick del click se
  // descarta sin moverse).
  const formSectionRef = useRef<HTMLDivElement>(null);

  // Fecha de hoy en formato YYYY-MM-DD, hora local del terapeuta — tope inferior
  // para el input de fecha y guard en el alta (no se puede bloquear el pasado).
  const hoy = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  const fetchBlockedPeriods = async () => {
    setBlockLoadStatus('loading');
    setBlockError('');
    try {
      const { data, error } = await supabase
        .from('therapist_blocked_periods')
        .select('*')
        .eq('terapeuta_id', targetTherapistId)
        .eq('activo', true)
        .order('fecha_inicio');

      if (error) {
        if (
          error.code === '42P01' ||
          error.message?.includes('does not exist') ||
          error.message?.includes('relation') ||
          error.code === 'PGRST200'
        ) {
          setBlockError(
            'La tabla de bloqueos no está configurada. ' +
            'Ejecutá sql/migrations/fix_bloqueos_horarios_2026_07_04.sql en Supabase.'
          );
        } else {
          setBlockError('No se pudieron cargar los días bloqueados. Intentá de nuevo.');
        }
        setBlockLoadStatus('error');
        return;
      }

      setBlockedPeriods(data || []);
      setBlockLoadStatus('loaded');
    } catch {
      setBlockError('Error inesperado al cargar los bloqueos.');
      setBlockLoadStatus('error');
    }
  };

  const fetchHistory = async () => {
    setHistoryLoadStatus('loading');
    try {
      const { data, error } = await supabase
        .from('therapist_blocked_periods_history')
        .select('*')
        .eq('terapeuta_id', targetTherapistId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        setHistoryLoadStatus('error');
        return;
      }

      setHistory(data || []);
      setHistoryLoadStatus('loaded');
    } catch {
      setHistoryLoadStatus('error');
    }
  };

  useEffect(() => {
    fetchBlockedPeriods();
    fetchHistory();
  }, [targetTherapistId]); // eslint-disable-line react-hooks/exhaustive-deps

  // FUNC-097 punto 3 — intervalo [inicio, fin) en epoch ms de un bloqueo,
  // existente o candidato. Día(s) completo(s) cubre desde las 00:00 del primer
  // día hasta las 23:59 del último; un bloqueo parcial es siempre de un solo
  // día (el form no ofrece fechaFin cuando diaCompleto es false).
  const buildBlockInterval = (bp: {
    fecha_inicio: string;
    fecha_fin: string | null;
    hora_inicio: string | null;
    hora_fin: string | null;
    todo_el_dia: boolean;
  }): { startMs: number; endMs: number } => {
    if (bp.todo_el_dia || !bp.hora_inicio || !bp.hora_fin) {
      const fin = bp.fecha_fin || bp.fecha_inicio;
      return {
        startMs: new Date(toSantiagoISO(bp.fecha_inicio, '00:00')).getTime(),
        endMs: new Date(toSantiagoISO(fin, '23:59')).getTime() + 60_000,
      };
    }
    return {
      startMs: new Date(toSantiagoISO(bp.fecha_inicio, bp.hora_inicio.slice(0, 5))).getTime(),
      endMs: new Date(toSantiagoISO(bp.fecha_inicio, bp.hora_fin.slice(0, 5))).getTime(),
    };
  };

  const intervalsOverlap = (a: { startMs: number; endMs: number }, b: { startMs: number; endMs: number }) =>
    a.startMs < b.endMs && b.startMs < a.endMs;

  const handleAddBlock = async () => {
    if (!newBlock.fechaInicio) return;
    if (!newBlock.diaCompleto && (!newBlock.horaInicio || !newBlock.horaFin)) return;
    if (newBlock.fechaInicio < hoy) {
      setBlockError('No podés crear un bloqueo en una fecha que ya pasó.');
      setBlockSaveStatus('error');
      return;
    }
    if (newBlock.diaCompleto && newBlock.fechaFin && newBlock.fechaFin < newBlock.fechaInicio) {
      setBlockError('La fecha de término no puede ser anterior a la fecha de inicio.');
      setBlockSaveStatus('error');
      return;
    }

    const candidate = buildBlockInterval({
      fecha_inicio: newBlock.fechaInicio,
      fecha_fin: newBlock.diaCompleto ? (newBlock.fechaFin || null) : null,
      hora_inicio: newBlock.diaCompleto ? null : newBlock.horaInicio,
      hora_fin: newBlock.diaCompleto ? null : newBlock.horaFin,
      todo_el_dia: newBlock.diaCompleto,
    });

    // FUNC-097 punto 3a — solapamiento contra otros bloqueos ya cargados.
    // blockedPeriods ya está scopeado a targetTherapistId — sin query nueva.
    const clashingBlock = blockedPeriods.find(bp => intervalsOverlap(candidate, buildBlockInterval(bp)));
    if (clashingBlock) {
      setBlockError(`Se superpone con un bloqueo existente: ${formatRangoBloqueo(clashingBlock)}.`);
      setBlockSaveStatus('error');
      return;
    }

    setBlockSaveStatus('saving');
    setBlockError('');

    // FUNC-097 punto 3b — solapamiento contra citas ya agendadas. Query
    // acotada al rango candidato, select directo a `consultas` (mismo patrón
    // que TherapistCalendar.tsx / Consultations.tsx, sin RPC nueva). Fail
    // closed: si la verificación falla no se deja guardar — nunca proceder
    // sobre datos parciales o no verificados, mismo criterio que el resto del
    // componente.
    const rangeFin = newBlock.diaCompleto ? (newBlock.fechaFin || newBlock.fechaInicio) : newBlock.fechaInicio;
    const { data: consultasEnRango, error: consultasError } = await supabase
      .from('consultas')
      .select('id, fecha_hora, duracion, estado')
      .eq('terapeuta_id', targetTherapistId)
      .gte('fecha_hora', toSantiagoISO(newBlock.fechaInicio, '00:00'))
      .lte('fecha_hora', toSantiagoISO(rangeFin, '23:59'))
      .neq('estado', 'cancelada');

    if (consultasError) {
      setBlockError('No se pudo verificar si hay citas agendadas en ese rango. Intentá de nuevo.');
      setBlockSaveStatus('error');
      return;
    }

    const clashingConsulta = (consultasEnRango ?? []).find(c => {
      const start = new Date(c.fecha_hora).getTime();
      const end = start + c.duracion * 60_000;
      return intervalsOverlap(candidate, { startMs: start, endMs: end });
    });

    if (clashingConsulta) {
      const fecha = new Date(clashingConsulta.fecha_hora);
      setBlockError(
        `Hay una cita agendada dentro de ese rango (${toSantiagoDateStr(fecha)} · ${toSantiagoTime(fecha)}). ` +
        'Reagendá o cancelá la cita antes de bloquear ese horario.'
      );
      setBlockSaveStatus('error');
      return;
    }

    try {
      const { data: inserted, error } = await supabase
        .from('therapist_blocked_periods')
        .insert({
          terapeuta_id: targetTherapistId,
          fecha_inicio: newBlock.fechaInicio,
          fecha_fin: newBlock.diaCompleto ? (newBlock.fechaFin || null) : null,
          hora_inicio: newBlock.diaCompleto ? null : newBlock.horaInicio,
          hora_fin: newBlock.diaCompleto ? null : newBlock.horaFin,
          todo_el_dia: newBlock.diaCompleto,
          tipo: newBlock.tipo,
          motivo: newBlock.motivo.trim() || null,
        })
        .select()
        .single();

      if (error) {
        setBlockError('No se pudo agregar el bloqueo. Intentá de nuevo.');
        setBlockSaveStatus('error');
        return;
      }

      setBlockedPeriods(prev => [...prev, inserted].sort((a, b) => a.fecha_inicio.localeCompare(b.fecha_inicio)));
      setNewBlock({ fechaInicio: '', fechaFin: '', horaInicio: '', horaFin: '', motivo: '', tipo: 'personal', diaCompleto: false });
      setBlockSaveStatus('idle');
      fetchHistory();
    } catch {
      setBlockError('Error inesperado al guardar el bloqueo.');
      setBlockSaveStatus('error');
    }
  };

  const handleCalendarDayClick = (dateStr: string) => {
    setNewBlock(prev => ({ ...prev, fechaInicio: dateStr }));
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        formSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  const selectedTherapistOption = isDirector
    ? terapeutasOptions.find(t => t.id === targetTherapistId)
    : null;

  const handleDeleteBlock = async (id: string) => {
    setBlockError('');
    try {
      const { error, count } = await supabase
        .from('therapist_blocked_periods')
        .delete({ count: 'exact' })
        .eq('id', id);

      if (error) {
        setBlockError('No se pudo eliminar el bloqueo. Intentá de nuevo.');
        setBlockSaveStatus('error');
        return;
      }

      if (count === 0) {
        // El DELETE no afectó filas (RLS o el bloqueo ya no existe) — no removemos
        // de la lista optimistamente para no ocultar un bloqueo que sigue vivo.
        setBlockError('No se pudo eliminar el bloqueo. Recargá la página e intentá de nuevo.');
        setBlockSaveStatus('error');
        fetchBlockedPeriods();
        return;
      }

      setBlockedPeriods(prev => prev.filter(b => b.id !== id));
      fetchHistory();
    } catch {
      setBlockError('Error inesperado al eliminar el bloqueo.');
      setBlockSaveStatus('error');
    }
  };

  return (
    <div className="availability-config">
      <div className="availability-header">
        <div>
          <h2 className="availability-title">Horarios</h2>
          <p className="availability-subtitle">Bloqueá los días y las horas en que no vas a atender</p>
        </div>
      </div>

      {isDirector && terapeutasOptions.length > 0 && (
        <div className="availability-therapist-select-row">
          <label className="availability-therapist-select-label" htmlFor="bp-terapeuta">
            Gestionando bloqueos de
          </label>
          <select
            id="bp-terapeuta"
            className="blocked-periods-input availability-therapist-select"
            value={targetTherapistId}
            onChange={e => setTargetTherapistId(e.target.value)}
          >
            {terapeutasOptions.map(t => (
              <option key={t.id} value={t.id}>
                {t.nombre} {t.apellido}{t.id === therapist.id ? ' (vos)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="blocked-periods-section">
        <div className="blocked-periods-header">
          <h3 className="blocked-periods-title">Bloqueos de agenda</h3>
          <p className="blocked-periods-description">
            Rangos de fecha/hora donde {selectedTherapistOption ? `${selectedTherapistOption.nombre} no está` : 'no estás'} disponible para agendar. Se aplican de inmediato al booking.
          </p>
        </div>

        <AvailabilityCalendar
          blockedPeriods={blockedPeriods}
          targetTherapistId={targetTherapistId}
          onDayClick={handleCalendarDayClick}
        />

        {blockError && blockLoadStatus !== 'loading' && (
          <div className="availability-error">
            <i className="fas fa-exclamation-triangle" />
            <span>{blockError}</span>
          </div>
        )}

        <div className="blocked-periods-form" ref={formSectionRef}>
          <div className="blocked-periods-toggle-row">
            <span className="blocked-periods-toggle-label">Día(s) completo(s)</span>
            <button
              type="button"
              role="switch"
              aria-checked={newBlock.diaCompleto}
              aria-label="Bloquear día(s) completo(s)"
              className={`theme-switch ${newBlock.diaCompleto ? 'theme-switch--on' : ''}`}
              onClick={() => setNewBlock(prev => ({
                ...prev,
                diaCompleto: !prev.diaCompleto,
                fechaFin: '',
                horaInicio: '',
                horaFin: '',
              }))}
            >
              <span className="theme-switch__knob" />
            </button>
          </div>
          <p className="blocked-periods-toggle-hint">
            {newBlock.diaCompleto
              ? 'Bloquea uno o varios días enteros — nada se puede agendar esos días.'
              : 'Bloquea un rango de horas dentro de un mismo día.'}
          </p>

          <div className="blocked-periods-form-row">
            <div className="blocked-periods-field">
              <label className="blocked-periods-label" htmlFor="bp-fecha-inicio">
                {newBlock.diaCompleto ? 'Desde' : 'Fecha'}
              </label>
              <input
                id="bp-fecha-inicio"
                type="date"
                className="blocked-periods-input"
                value={newBlock.fechaInicio}
                min={hoy}
                onChange={e => setNewBlock(prev => ({ ...prev, fechaInicio: e.target.value }))}
              />
            </div>

            {newBlock.diaCompleto && (
              <div className="blocked-periods-field">
                <label className="blocked-periods-label" htmlFor="bp-fecha-fin">Hasta (opcional)</label>
                <input
                  id="bp-fecha-fin"
                  type="date"
                  className="blocked-periods-input"
                  value={newBlock.fechaFin}
                  min={newBlock.fechaInicio || hoy}
                  onChange={e => setNewBlock(prev => ({ ...prev, fechaFin: e.target.value }))}
                />
              </div>
            )}

            {!newBlock.diaCompleto && (
              <>
                <div className="blocked-periods-field">
                  <label className="blocked-periods-label" htmlFor="bp-inicio">Hora inicio</label>
                  <input
                    id="bp-inicio"
                    type="time"
                    className="blocked-periods-input"
                    value={newBlock.horaInicio}
                    onChange={e => setNewBlock(prev => ({ ...prev, horaInicio: e.target.value }))}
                  />
                </div>
                <div className="blocked-periods-field">
                  <label className="blocked-periods-label" htmlFor="bp-fin">Hora fin</label>
                  <input
                    id="bp-fin"
                    type="time"
                    className="blocked-periods-input"
                    value={newBlock.horaFin}
                    onChange={e => setNewBlock(prev => ({ ...prev, horaFin: e.target.value }))}
                  />
                </div>
              </>
            )}

            <div className="blocked-periods-field">
              <label className="blocked-periods-label" htmlFor="bp-tipo">Tipo</label>
              <select
                id="bp-tipo"
                className="blocked-periods-input"
                value={newBlock.tipo}
                onChange={e => setNewBlock(prev => ({ ...prev, tipo: e.target.value }))}
              >
                {Object.entries(TIPO_BLOQUEO_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div className="blocked-periods-field">
              <label className="blocked-periods-label" htmlFor="bp-motivo">Motivo</label>
              <input
                id="bp-motivo"
                type="text"
                className="blocked-periods-input"
                placeholder="Opcional"
                value={newBlock.motivo}
                onChange={e => setNewBlock(prev => ({ ...prev, motivo: e.target.value }))}
              />
            </div>
          </div>

          <div className="blocked-periods-form-footer">
            <button
              type="button"
              className="blocked-periods-add-btn"
              onClick={handleAddBlock}
              disabled={
                blockSaveStatus === 'saving' ||
                !newBlock.fechaInicio ||
                (!newBlock.diaCompleto && (!newBlock.horaInicio || !newBlock.horaFin))
              }
            >
              {blockSaveStatus === 'saving' ? 'Verificando y agregando...' : 'Agregar bloqueo'}
            </button>
          </div>
        </div>

        {blockLoadStatus === 'loading' && (
          <div className="availability-loading">
            <div className="spinner" />
            <p>Cargando bloqueos...</p>
          </div>
        )}

        {blockLoadStatus === 'loaded' && blockedPeriods.length === 0 && (
          <p className="blocked-periods-empty">No hay bloqueos de agenda configurados.</p>
        )}

        {blockedPeriods.length > 0 && (
          <ul className="blocked-periods-list">
            {blockedPeriods.map(bp => (
              <li key={bp.id} className="blocked-periods-item">
                <div className="blocked-periods-item-info">
                  <span className="blocked-periods-item-date">{formatRangoBloqueo(bp)}</span>
                  <span className="blocked-periods-item-tipo">{TIPO_BLOQUEO_LABELS[bp.tipo] ?? bp.tipo}</span>
                  {bp.motivo && (
                    <span className="blocked-periods-item-motivo">{bp.motivo}</span>
                  )}
                </div>
                {confirmDeleteId === bp.id ? (
                  <div className="blocked-periods-confirm">
                    <span className="blocked-periods-confirm-text">¿Eliminar?</span>
                    <button
                      type="button"
                      className="blocked-periods-confirm-yes"
                      onClick={() => { setConfirmDeleteId(null); handleDeleteBlock(bp.id); }}
                    >
                      Sí
                    </button>
                    <button
                      type="button"
                      className="blocked-periods-confirm-no"
                      onClick={() => setConfirmDeleteId(null)}
                    >
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="blocked-periods-delete-btn"
                    onClick={() => setConfirmDeleteId(bp.id)}
                    title="Eliminar bloqueo"
                    aria-label="Eliminar bloqueo"
                  >
                    <i className="fas fa-times" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="blocked-history-section">
        <button
          type="button"
          className="blocked-history-toggle"
          onClick={() => setHistoryOpen(prev => !prev)}
        >
          <i className={`fas fa-chevron-${historyOpen ? 'down' : 'right'}`} />
          Historial de bloqueos{history.length > 0 ? ` (${history.length})` : ''}
        </button>

        {historyOpen && (
          <div className="blocked-history-body">
            {historyLoadStatus === 'loading' && (
              <div className="availability-loading">
                <div className="spinner" />
                <p>Cargando historial...</p>
              </div>
            )}

            {historyLoadStatus === 'error' && (
              <p className="blocked-periods-empty">No se pudo cargar el historial.</p>
            )}

            {historyLoadStatus === 'loaded' && history.length === 0 && (
              <p className="blocked-periods-empty">Todavía no hay movimientos registrados.</p>
            )}

            {history.length > 0 && (
              <ul className="blocked-history-list">
                {history.map(entry => (
                  <li key={entry.id} className={`blocked-history-item blocked-history-item--${entry.accion}`}>
                    <div className="blocked-history-item-main">
                      <span className="blocked-history-item-accion">
                        {ACCION_HISTORIAL_LABELS[entry.accion]}
                      </span>
                      <span className="blocked-history-item-date">{formatRangoBloqueo(entry)}</span>
                    </div>
                    <div className="blocked-history-item-meta">
                      {entry.tipo && <span>{TIPO_BLOQUEO_LABELS[entry.tipo] ?? entry.tipo}</span>}
                      {entry.motivo && <span>{entry.motivo}</span>}
                      <span>
                        {new Date(entry.created_at).toLocaleString('es-CL', {
                          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                        })}
                        {entry.realizado_por_nombre ? ` · ${entry.realizado_por_nombre}` : ''}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface PublicProfileData {
  slug: string | null;
  biografia: string | null;
  filosofia_personal: string | null;
  enfoque_terapeutico: string[] | null;
  idiomas: string[] | null;
  anos_experiencia: number | null;
  educacion: Array<{ degree?: string; institution?: string; year?: string }> | null;
  foto_url: string | null;
  imagen_perfil: string | null;
}

/** Campo de tags editable — Enter o coma agrega un valor, la ✕ lo quita. */
const TagField: React.FC<{
  label: string;
  values: string[];
  placeholder: string;
  onChange: (next: string[]) => void;
}> = ({ label, values, placeholder, onChange }) => {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const value = draft.trim();
    if (value && !values.includes(value)) onChange([...values, value]);
    setDraft('');
  };

  return (
    <div className="pf-field">
      <label className="pf-field__label">{label}</label>
      <div className="pf-tags">
        {values.map(value => (
          <span key={value} className="pf-tag">
            {value}
            <button
              type="button"
              className="pf-tag__x"
              onClick={() => onChange(values.filter(v => v !== value))}
              aria-label={`Quitar ${value}`}
            >
              <i className="fas fa-times"></i>
            </button>
          </span>
        ))}
        <input
          className="pf-tags__input"
          value={draft}
          placeholder={values.length ? '' : placeholder}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              commit();
            }
          }}
          onBlur={commit}
        />
      </div>
    </div>
  );
};

const TherapistProfile: React.FC<{
  therapist: AuthenticatedTherapist;
  onLogout: () => void;
}> = ({ therapist, onLogout }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [formData, setFormData] = useState({
    nombre:      therapist.nombre,
    apellido:    therapist.apellido,
    telefono:    therapist.telefono ?? '',
    especialidad: therapist.especialidad ?? '',
    meet_url:    therapist.meet_url ?? '',
  });
  const [displayData, setDisplayData] = useState({ ...formData });

  const [tab, setTab] = useState<'identidad' | 'publico' | 'ajustes'>('identidad');

  // ── Perfil público: columnas extra de `terapeutas` que no vienen en la sesión ──
  const [pub, setPub] = useState<PublicProfileData | null>(null);
  const [pubForm, setPubForm] = useState({
    biografia: '',
    filosofia_personal: '',
    enfoque_terapeutico: [] as string[],
    idiomas: [] as string[],
  });
  const [pubEditing, setPubEditing] = useState(false);
  const [pubStatus, setPubStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('terapeutas')
        .select('slug, biografia, filosofia_personal, enfoque_terapeutico, idiomas, anos_experiencia, educacion, foto_url, imagen_perfil')
        .eq('id', therapist.id)
        .maybeSingle();
      if (cancelled || !data) return;
      setPub(data as PublicProfileData);
      setPubForm({
        biografia: data.biografia ?? '',
        filosofia_personal: data.filosofia_personal ?? '',
        enfoque_terapeutico: Array.isArray(data.enfoque_terapeutico) ? data.enfoque_terapeutico : [],
        idiomas: Array.isArray(data.idiomas) ? data.idiomas : [],
      });
    })();
    return () => { cancelled = true; };
  }, [therapist.id]);

  const resetPubForm = () => setPubForm({
    biografia: pub?.biografia ?? '',
    filosofia_personal: pub?.filosofia_personal ?? '',
    enfoque_terapeutico: Array.isArray(pub?.enfoque_terapeutico) ? pub!.enfoque_terapeutico! : [],
    idiomas: Array.isArray(pub?.idiomas) ? pub!.idiomas! : [],
  });

  const handleSavePublic = async () => {
    setPubStatus('saving');
    const { error } = await supabase
      .from('terapeutas')
      .update({
        biografia: pubForm.biografia.trim() || null,
        filosofia_personal: pubForm.filosofia_personal.trim() || null,
        enfoque_terapeutico: pubForm.enfoque_terapeutico.length ? pubForm.enfoque_terapeutico : null,
        idiomas: pubForm.idiomas.length ? pubForm.idiomas : null,
      })
      .eq('id', therapist.id);
    if (error) {
      setPubStatus('error');
      return;
    }
    setPub(prev => (prev ? { ...prev, ...pubForm } : prev));
    setPubStatus('saved');
    setPubEditing(false);
    setTimeout(() => setPubStatus('idle'), 2500);
  };

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaveStatus('saving');
    const rawMeet = formData.meet_url.trim();
    const normalizedMeet = rawMeet
      ? /^https?:\/\//i.test(rawMeet) ? rawMeet : `https://${rawMeet}`
      : null;
    const { error } = await supabase
      .from('terapeutas')
      .update({
        nombre:       formData.nombre.trim(),
        apellido:     formData.apellido.trim(),
        telefono:     formData.telefono.trim() || null,
        titulo:       formData.especialidad.trim() || null,
        meet_url:     normalizedMeet,
      })
      .eq('id', therapist.id);

    if (error) {
      setSaveStatus('error');
      return;
    }
    setDisplayData({ ...formData, meet_url: normalizedMeet ?? '' });
    setSaveStatus('saved');
    setIsEditing(false);
    setTimeout(() => setSaveStatus('idle'), 2500);
  };

  const handleCancel = () => {
    setFormData({ ...displayData });
    setIsEditing(false);
    setSaveStatus('idle');
  };

  const [meetCopied, setMeetCopied] = useState(false);

  const handleOpenMeet = async () => {
    if (!displayData.meet_url) return;
    const url = /^https?:\/\//i.test(displayData.meet_url)
      ? displayData.meet_url
      : `https://${displayData.meet_url}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // portapapeles denegado — sala igual se abre
    }
    setMeetCopied(true);
    setTimeout(() => setMeetCopied(false), 3000);
  };

  const avatarUrl = therapist.avatar || pub?.foto_url || pub?.imagen_perfil || null;
  const initials = `${displayData.nombre.charAt(0)}${displayData.apellido.charAt(0)}`.toUpperCase();
  const publicUrl = pub?.slug ? `/terapista/${pub.slug}` : null;
  const openPublicPage = () => {
    if (publicUrl) window.open(publicUrl, '_blank', 'noopener,noreferrer');
  };
  const enfoque = Array.isArray(pub?.enfoque_terapeutico) ? pub!.enfoque_terapeutico! : [];
  const idiomas = Array.isArray(pub?.idiomas) ? pub!.idiomas! : [];
  const educacion = Array.isArray(pub?.educacion) ? pub!.educacion! : [];

  return (
    <div className="pf">
      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <header className="pf-hero">
        <div className="pf-hero__cover" aria-hidden="true"></div>
        <div className="pf-hero__body">
          <div className="pf-hero__avatar">
            {avatarUrl ? <img src={avatarUrl} alt="" /> : <span>{initials}</span>}
          </div>
          <div className="pf-hero__identity">
            <div className="pf-hero__namerow">
              <h2 className="pf-hero__name">{displayData.nombre} {displayData.apellido}</h2>
              {therapist.rol === 'director' && <span className="director-badge">Director</span>}
            </div>
            <p className="pf-hero__title">{displayData.especialidad || 'Sin especialidad'}</p>
            <div className="pf-hero__chips">
              {therapist.numeroLicencia && (
                <span className="pf-chip"><i className="fas fa-id-badge"></i> Lic. {therapist.numeroLicencia}</span>
              )}
              {typeof pub?.anos_experiencia === 'number' && pub.anos_experiencia > 0 && (
                <span className="pf-chip"><i className="fas fa-clock"></i> {pub.anos_experiencia} años de experiencia</span>
              )}
              {idiomas.slice(0, 3).map(idioma => (
                <span key={idioma} className="pf-chip"><i className="fas fa-language"></i> {idioma}</span>
              ))}
            </div>
          </div>
          {publicUrl && (
            <button type="button" className="pf-hero__link" onClick={openPublicPage}>
              <i className="fas fa-external-link-alt"></i> Ver mi página
            </button>
          )}
        </div>
      </header>

      {/* ── TABS ──────────────────────────────────────────────────────── */}
      <nav className="pf-tabs" role="tablist" aria-label="Secciones del perfil">
        {([
          ['identidad', 'Identidad', 'fa-user'],
          ['publico', 'Perfil público', 'fa-globe'],
          ['ajustes', 'Ajustes', 'fa-gear'],
        ] as const).map(([id, label, icon]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`pf-tab ${tab === id ? 'pf-tab--active' : ''}`}
            onClick={() => setTab(id)}
          >
            <i className={`fas ${icon}`}></i>
            <span>{label}</span>
          </button>
        ))}
      </nav>

      {/* ── PANEL: IDENTIDAD ──────────────────────────────────────────── */}
      {tab === 'identidad' && (
        <div className="pf-panel">
          {saveStatus === 'saved' && (
            <div className="pf-feedback pf-feedback--ok">
              <i className="fas fa-check-circle"></i> Perfil actualizado correctamente.
            </div>
          )}
          {saveStatus === 'error' && (
            <div className="pf-feedback pf-feedback--err">
              <i className="fas fa-exclamation-circle"></i> No se pudo guardar. Intentá nuevamente.
            </div>
          )}

          {isEditing ? (
            <div className="pf-card pf-form">
              <div className="pf-form__row">
                <div className="pf-field">
                  <label className="pf-field__label">Nombre</label>
                  <input className="pf-input" type="text" value={formData.nombre}
                    onChange={e => handleChange('nombre', e.target.value)} />
                </div>
                <div className="pf-field">
                  <label className="pf-field__label">Apellido</label>
                  <input className="pf-input" type="text" value={formData.apellido}
                    onChange={e => handleChange('apellido', e.target.value)} />
                </div>
              </div>
              <div className="pf-form__row">
                <div className="pf-field">
                  <label className="pf-field__label">Teléfono</label>
                  <input className="pf-input" type="tel" value={formData.telefono}
                    onChange={e => handleChange('telefono', e.target.value)} placeholder="+56 9 XXXX XXXX" />
                </div>
                <div className="pf-field">
                  <label className="pf-field__label">Especialidad</label>
                  <input className="pf-input" type="text" value={formData.especialidad}
                    onChange={e => handleChange('especialidad', e.target.value)} />
                </div>
              </div>
              <div className="pf-field">
                <label className="pf-field__label">
                  <i className="fas fa-video" style={{ marginRight: 6, color: 'var(--apple-blue)' }}></i>
                  Sala de Google Meet (sesiones online)
                </label>
                <input className="pf-input" type="url" value={formData.meet_url}
                  onChange={e => handleChange('meet_url', e.target.value)}
                  placeholder="https://meet.google.com/xxx-yyyy-zzz" />
                <span className="pf-hint">
                  Pegá el link de tu sala personal. Se envía automáticamente a los pacientes que agenden sesión online.
                </span>
              </div>
              <div className="pf-actions">
                <button className="pf-btn pf-btn--primary" onClick={handleSave} disabled={saveStatus === 'saving'}>
                  {saveStatus === 'saving'
                    ? <><i className="fas fa-spinner fa-spin"></i> Guardando...</>
                    : <><i className="fas fa-check"></i> Guardar</>}
                </button>
                <button className="pf-btn pf-btn--secondary" onClick={handleCancel} disabled={saveStatus === 'saving'}>
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="pf-datagrid">
                <div className="pf-cell">
                  <span className="pf-cell__k">Email</span>
                  <span className="pf-cell__v">{therapist.email}</span>
                </div>
                <div className="pf-cell">
                  <span className="pf-cell__k">Teléfono</span>
                  <span className="pf-cell__v">{displayData.telefono || '—'}</span>
                </div>
                <div className="pf-cell">
                  <span className="pf-cell__k">Especialidad</span>
                  <span className="pf-cell__v">{displayData.especialidad || '—'}</span>
                </div>
                <div className="pf-cell">
                  <span className="pf-cell__k">RUT</span>
                  <span className="pf-cell__v">{therapist.rut}</span>
                </div>
                {therapist.numeroLicencia && (
                  <div className="pf-cell">
                    <span className="pf-cell__k">N.° Licencia</span>
                    <span className="pf-cell__v">{therapist.numeroLicencia}</span>
                  </div>
                )}
                <div className="pf-cell pf-cell--meet">
                  <span className="pf-cell__k">
                    <i className="fas fa-video" style={{ marginRight: 6, color: 'var(--apple-blue)' }}></i>
                    Sala Meet
                  </span>
                  {displayData.meet_url ? (
                    <button
                      className={`meet-quick-action__btn meet-quick-action__btn--inline${meetCopied ? ' meet-quick-action__btn--copied' : ''}`}
                      onClick={handleOpenMeet}
                    >
                      {meetCopied
                        ? <><i className="fas fa-check"></i> Sala abierta · enlace copiado</>
                        : <><i className="fas fa-external-link-alt"></i> Abrir sala</>}
                    </button>
                  ) : (
                    <span className="pf-cell__v pf-cell__v--muted">No configurada</span>
                  )}
                </div>
              </div>
              <div className="pf-actions">
                <button className="pf-btn pf-btn--primary" onClick={() => setIsEditing(true)}>
                  <i className="fas fa-pen"></i> Editar identidad
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── PANEL: PERFIL PÚBLICO ─────────────────────────────────────── */}
      {tab === 'publico' && (
        <div className="pf-panel">
          <div className="pf-preview">
            <span className="pf-preview__tag"><i className="fas fa-eye"></i> Así te ven los pacientes</span>
            <div className="pf-preview__card">
              <div className="pf-preview__avatar">
                {avatarUrl ? <img src={avatarUrl} alt="" /> : <span>{initials}</span>}
              </div>
              <div className="pf-preview__meta">
                <strong>{displayData.nombre} {displayData.apellido}</strong>
                <span>{displayData.especialidad || 'Sin especialidad'}</span>
                <p>{pub?.biografia || 'Todavía no cargaste tu biografía pública.'}</p>
              </div>
            </div>
            {publicUrl ? (
              <button type="button" className="pf-btn pf-btn--ghost" onClick={openPublicPage}>
                <i className="fas fa-external-link-alt"></i> Ver mi página pública
              </button>
            ) : (
              <p className="pf-hint">Tu página pública se activa cuando el equipo asigna tu enlace.</p>
            )}
          </div>

          {pubStatus === 'saved' && (
            <div className="pf-feedback pf-feedback--ok">
              <i className="fas fa-check-circle"></i> Perfil público actualizado.
            </div>
          )}
          {pubStatus === 'error' && (
            <div className="pf-feedback pf-feedback--err">
              <i className="fas fa-exclamation-circle"></i> No se pudo guardar. Intentá nuevamente.
            </div>
          )}

          {pubEditing ? (
            <div className="pf-card pf-form">
              <div className="pf-field">
                <label className="pf-field__label">Biografía</label>
                <textarea className="pf-textarea" rows={5} value={pubForm.biografia}
                  onChange={e => setPubForm(p => ({ ...p, biografia: e.target.value }))}
                  placeholder="Contá tu trayectoria, tu forma de trabajar y qué puede esperar un paciente en tus sesiones." />
              </div>
              <div className="pf-field">
                <label className="pf-field__label">Filosofía personal</label>
                <textarea className="pf-textarea" rows={3} value={pubForm.filosofia_personal}
                  onChange={e => setPubForm(p => ({ ...p, filosofia_personal: e.target.value }))}
                  placeholder="Una frase o párrafo que resuma tu mirada del proceso terapéutico." />
              </div>
              <TagField
                label="Enfoque terapéutico"
                values={pubForm.enfoque_terapeutico}
                placeholder="Escribí y presioná Enter — ej: Cognitivo-conductual"
                onChange={next => setPubForm(p => ({ ...p, enfoque_terapeutico: next }))}
              />
              <TagField
                label="Idiomas"
                values={pubForm.idiomas}
                placeholder="Escribí y presioná Enter — ej: Español"
                onChange={next => setPubForm(p => ({ ...p, idiomas: next }))}
              />
              <div className="pf-actions">
                <button className="pf-btn pf-btn--primary" onClick={handleSavePublic} disabled={pubStatus === 'saving'}>
                  {pubStatus === 'saving'
                    ? <><i className="fas fa-spinner fa-spin"></i> Guardando...</>
                    : <><i className="fas fa-check"></i> Guardar</>}
                </button>
                <button className="pf-btn pf-btn--secondary" disabled={pubStatus === 'saving'}
                  onClick={() => { setPubEditing(false); resetPubForm(); }}>
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div className="pf-card pf-readblock">
              <div className="pf-read">
                <span className="pf-read__k">Biografía</span>
                <p className="pf-read__v">{pub?.biografia || '—'}</p>
              </div>
              <div className="pf-read">
                <span className="pf-read__k">Filosofía personal</span>
                <p className="pf-read__v">{pub?.filosofia_personal || '—'}</p>
              </div>
              <div className="pf-read">
                <span className="pf-read__k">Enfoque terapéutico</span>
                <div className="pf-chips-row">
                  {enfoque.length
                    ? enfoque.map(x => <span key={x} className="pf-chip">{x}</span>)
                    : <span className="pf-read__v">—</span>}
                </div>
              </div>
              <div className="pf-read">
                <span className="pf-read__k">Idiomas</span>
                <div className="pf-chips-row">
                  {idiomas.length
                    ? idiomas.map(x => <span key={x} className="pf-chip">{x}</span>)
                    : <span className="pf-read__v">—</span>}
                </div>
              </div>
              {educacion.length > 0 && (
                <div className="pf-read">
                  <span className="pf-read__k">
                    Formación <span className="pf-read__note">· edición desde el portal en la próxima entrega</span>
                  </span>
                  <ul className="pf-edu">
                    {educacion.map((e, i) => (
                      <li key={i}>
                        <strong>{e.degree}</strong>
                        {e.institution ? ` — ${e.institution}` : ''}
                        {e.year ? ` (${e.year})` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="pf-actions">
                <button className="pf-btn pf-btn--primary" onClick={() => { resetPubForm(); setPubEditing(true); }}>
                  <i className="fas fa-pen"></i> Editar perfil público
                </button>
              </div>
            </div>
          )}

          <p className="pf-hint pf-hint--foot">
            <i className="fas fa-camera"></i> La foto de perfil y la edición de formación llegan en la próxima entrega.
          </p>
        </div>
      )}

      {/* ── PANEL: AJUSTES ────────────────────────────────────────────── */}
      {tab === 'ajustes' && (
        <div className="pf-panel">
          <ProfileAppearanceSection />
          <div className="pf-card pf-logout">
            <div className="pf-logout__text">
              <strong>Cerrar sesión</strong>
              <span>Vas a volver a la pantalla de acceso del portal.</span>
            </div>
            <button className="logout-btn-main" onClick={onLogout}>
              <i className="fas fa-sign-out-alt"></i> Cerrar sesión
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ProfileAppearanceSection: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div className="profile-appearance-section">
      <h3 className="profile-appearance-section__title">Apariencia</h3>
      <div className="profile-appearance-section__row">
        <div className="profile-appearance-section__label">
          <i className="fas fa-moon"></i>
          <span>Modo oscuro</span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={isDark}
          aria-label="Modo oscuro"
          className={`theme-switch ${isDark ? 'theme-switch--on' : ''}`}
          onClick={toggleTheme}
        >
          <span className="theme-switch__knob" />
        </button>
      </div>
    </div>
  );
};

// UX_VISION P1-7 — estado "En construcción" honesto. Antes mostraba KPIs y un
// gráfico de barras con datos hardcodeados (`weeklyData`) + un "+18% vs mes anterior"
// inventado; decisión de producto: no fingir un dashboard con datos demo.
const CashFlowView: React.FC<{ therapist: AuthenticatedTherapist }> = () => {
  return (
    <div className="cashflow-view">
      <div className="cashflow-header">
        <div>
          <h2 className="cashflow-title">
            <i className="fas fa-receipt" aria-hidden="true"></i> Flujo de Caja
          </h2>
          <p className="cashflow-subtitle">Módulo en desarrollo</p>
        </div>
      </div>

      <div className="sii-integration-card">
        <div className="sii-badge">Próximamente</div>
        <div className="sii-icon">
          <i className="fas fa-file-invoice"></i>
        </div>
        <h3 className="sii-title">Integración con SII</h3>
        <p className="sii-description">
          Todavía no está disponible. Cuando lo esté, vas a ver acá tus ingresos reales por
          semana y por mes, con emisión automática de boletas electrónicas y análisis de
          métricas fiscales sincronizado con el Servicio de Impuestos Internos de Chile.
        </p>
        <div className="sii-features">
          <div className="sii-feature">
            <i className="fas fa-check-circle"></i>
            <span>Ingresos reales por semana y mes</span>
          </div>
          <div className="sii-feature">
            <i className="fas fa-check-circle"></i>
            <span>Emisión automática de boletas</span>
          </div>
          <div className="sii-feature">
            <i className="fas fa-check-circle"></i>
            <span>Declaración mensual simplificada</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TherapistPortal;